/**
 * network/discovery.ts — pure discovery filtering + connection suggestions.
 *
 * Operates on RECORDED peers only. The distance filter is honest: it needs
 * recorded coordinates on BOTH the user and the peer — peers without
 * coordinates are excluded when a distance cap is demanded (unknown never
 * passes a demanded floor). Suggestions carry recorded-overlap basis only.
 */

import type {
  ConnectionSuggestion,
  DiscoveryFilters,
  NetworkProfile,
  PeerProfile,
} from './models';

export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

const eq = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
const has = (list: string[], v: string) => list.some((x) => eq(x, v));

export function discoverPeers(
  peers: PeerProfile[],
  filters: DiscoveryFilters,
  self: { interests: string[]; latitude: number | null; longitude: number | null },
): PeerProfile[] {
  return peers.filter((p) => {
    if (filters.verifiedOnly && !p.verified) return false;
    if (filters.city != null && (p.city == null || !eq(p.city, filters.city))) return false;
    if (filters.country != null && (p.country == null || !eq(p.country, filters.country))) return false;
    if (filters.profession != null &&
        (p.professionalTitle == null || !p.professionalTitle.toLowerCase().includes(filters.profession.toLowerCase()))) return false;
    if (filters.industry != null && !has(p.industries, filters.industry)) return false;
    if (filters.language != null && !has(p.languages, filters.language)) return false;
    if (filters.skill != null && !has(p.skills, filters.skill)) return false;
    if (filters.availability != null && p.availability !== filters.availability) return false;
    if (filters.minExperienceYears != null &&
        (p.remoteExperienceYears == null || p.remoteExperienceYears < filters.minExperienceYears)) return false;
    if (filters.sharedInterestsOnly &&
        !p.interests.some((i) => has(self.interests, i))) return false;
    if (filters.maxDistanceKm != null) {
      // Honest distance: both sides need recorded coordinates.
      if (self.latitude == null || self.longitude == null ||
          p.latitude == null || p.longitude == null) return false;
      if (haversineKm(self.latitude, self.longitude, p.latitude, p.longitude) > filters.maxDistanceKm) return false;
    }
    return true;
  });
}

export function suggestConnections(
  peers: PeerProfile[],
  connectedPeerIds: Set<string>,
  self: { currentBase: string | null; skills: string[]; interests: string[]; industries: string[] },
): ConnectionSuggestion[] {
  const suggestions: ConnectionSuggestion[] = [];
  for (const p of peers) {
    if (connectedPeerIds.has(p.id)) continue;
    const basis: string[] = [];
    if (self.currentBase != null && p.city != null && eq(p.city, self.currentBase)) {
      basis.push(`both in ${p.city}`);
    }
    const sharedSkills = p.skills.filter((s) => has(self.skills, s));
    if (sharedSkills.length > 0) basis.push(`shared skills: ${sharedSkills.join(', ')}`);
    const sharedInterests = p.interests.filter((i) => has(self.interests, i));
    if (sharedInterests.length > 0) basis.push(`shared interests: ${sharedInterests.join(', ')}`);
    const sharedIndustries = p.industries.filter((i) => has(self.industries, i));
    if (sharedIndustries.length > 0) basis.push(`same industry: ${sharedIndustries.join(', ')}`);
    if (basis.length > 0) {
      suggestions.push({ peerId: p.id, displayName: p.displayName, basis });
    }
  }
  // Most evidence first — deterministic tie-break by name.
  return suggestions.sort(
    (a, b) => b.basis.length - a.basis.length || a.displayName.localeCompare(b.displayName),
  );
}

export function computeCommunityTrust(components: {
  helpfulReports: number;
  meetupsAttended: number;
  contributions: number;
  missionParticipation: number;
  recommendations: number;
}): number | null {
  const signals =
    components.helpfulReports +
    components.meetupsAttended +
    components.contributions +
    components.missionParticipation +
    components.recommendations;
  if (signals < 3) return null; // too little recorded evidence to claim trust
  const score =
    Math.min(components.helpfulReports, 10) * 2 +
    Math.min(components.meetupsAttended, 10) * 2 +
    Math.min(components.contributions, 10) * 2 +
    Math.min(components.missionParticipation, 10) * 2 +
    Math.min(components.recommendations, 10) * 2;
  return Math.min(100, score);
}

export type { ConnectionSuggestion, NetworkProfile };
